import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import { call, put, select, takeEvery, all, takeLatest, take, race, fork, cancel, join, delay } from 'redux-saga/effects';
import ddt from '@wix/da-ddt';
import { getIsLoggedIn, getCurrentUserId } from '../../publicSession/selectors';
import { restrictLoggedOut } from '../../redux/loggedout';
import { selectors, withOffset } from '../../Stream';
import { fetchCollections, initializeDeviation, initializeDeviationCollections, commitCollections, commitCollectionsFail, commitCollectionsSuccess, createCollection, createCollectionFail, createCollectionSuccess, undoChanges, toggleCollection, changeFave, collectFave, changeFaveSuccess, changeFaveFail, requestClose, setFaveState, resetJustFaved, setPrivatelyCollectedState } from './actionCreators';
import { getUpdatedEntities, getChangeDetails, getSpecialDefaultFolderId, getIsDeviationInitialized, getSelectedFolderIds } from './selectors';
import { COLLECTIONS_PAGE_SIZE, PRIVATE_COLLECTIONS_STREAMID, PUBLIC_COLLECTIONS_STREAMID, UNDO_TIMEOUT } from './constants';
import { doFetchCollections, doFetchContainingCollectionIds, doCommitCollections, doCreateCollection, doFave, doUnfave } from './xhr';
import { addNotificationToQueue } from '../../Notifications/redux/actionCreators';
import { NotificationType } from '../../Notifications/redux/types';
import { ModalType } from '../../Modals/redux/types';
import { pushModal, popModal } from '../../Modals/redux/actionCreators';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { getStreamId } from './helpers';
const logger = ddt.createLogger('collections');
function* handleFetchStream(action) {
    const { streamId  } = action;
    if (![
        PUBLIC_COLLECTIONS_STREAMID,
        PRIVATE_COLLECTIONS_STREAMID
    ].includes(streamId)) {
        return;
    }
    yield put(fetchCollections(streamId));
}
function* handleFetchCollections(action) {
    const { streamId  } = action.payload;
    const { offset , limit  } = yield select((state)=>({
            offset: withOffset.selectors.getNextOffset(state, streamId),
            limit: selectors.getItemsPerFetch(state, streamId)
        }));
    try {
        const result = yield call(doFetchCollections, streamId === PRIVATE_COLLECTIONS_STREAMID, offset, limit);
        if (!result) {
            yield put(withOffset.actions.fetchFailure({
                streamId,
                errorMsg: 'fetch error'
            }));
            return;
        }
        const items = [];
        const entities = {
            collections: {}
        };
        result.results.forEach((collection)=>{
            items.push(collection.folderId);
            entities.collections[collection.folderId] = collection;
        });
        yield put(withOffset.actions.fetchSuccessDeduped({
            streamId,
            items,
            entities,
            hasMore: result.hasMore || false
        }));
    } catch (errorMsg) {
        yield put(withOffset.actions.fetchFailure({
            streamId,
            errorMsg
        }));
    }
}
function* initializeUser(isPrivateCollections) {
    const userId = yield select(getCurrentUserId);
    const streamId = getStreamId(isPrivateCollections);
    const streamParams = yield select(selectors.getStreamParams, streamId);
    if (streamParams && streamParams.userId) {
        if (streamParams.userId === userId) {
            logger.log('User collections already init', streamParams);
            return;
        }
        logger.log('Collection stream belongs to another user', streamParams);
        yield put(withOffset.actions.deleteStream(streamId));
    }
    logger.log('Initializing user collections');
    yield put(withOffset.actions.initialize({
        streamId,
        itemsPerFetch: COLLECTIONS_PAGE_SIZE,
        items: [],
        hasMore: true,
        streamParams: {
            type: 'userCollections',
            userId
        }
    }));
    // do initial fetch
    yield put(fetchCollections(streamId));
}
function* handleInitializeDeviation(action) {
    if (!action.payload) {
        return;
    }
    const { deviationId , isPrivateCollections , faveCount , preselectDefaultFolder , ignorePreselectedCollections  } = action.payload;
    const isLoggedIn = yield select(getIsLoggedIn);
    if (!isLoggedIn) {
        yield put(restrictLoggedOut({
            restrictedActionName: ReferralType.FAVOURITE
        }));
        return;
    }
    yield call(initializeUser, isPrivateCollections);
    logger.log('Initializing deviation', deviationId);
    const isAlreadyInit = yield select(getIsDeviationInitialized, deviationId, isPrivateCollections);
    if (!isAlreadyInit) {
        logger.log('Fetching containing collections');
        const result = yield call(doFetchContainingCollectionIds, deviationId, isPrivateCollections);
        if (result) {
            logger.log('Initializing deviation collections', deviationId, result);
            yield put(initializeDeviationCollections(deviationId, isPrivateCollections, result, faveCount, ignorePreselectedCollections));
        }
    }
    // in case of unfaved deviation, we init to featured
    const selected = yield select(getSelectedFolderIds, deviationId, isPrivateCollections);
    if (!selected.length) {
        if (!preselectDefaultFolder) {
            return;
        }
        const featuredId = yield select(getSpecialDefaultFolderId, isPrivateCollections);
        logger.log('Setting selection to default featured', featuredId);
        if (featuredId) {
            yield put(toggleCollection(featuredId, deviationId, isPrivateCollections));
        }
    }
}
function* saveSelection(deviationId, selected, isPrivateCollections) {
    logger.log('Saving selection', selected);
    // save results
    const result = yield call(doCommitCollections, selected, deviationId, isPrivateCollections);
    const failedFolders = result && result.failedFolders ? result.failedFolders : !result || !result.success ? selected : [];
    const successFolders = selected.filter((folderId)=>!failedFolders.includes(folderId));
    logger.log('Save complete', {
        failedFolders,
        successFolders
    });
    return {
        failedFolders,
        successFolders
    };
}
function* handleCommitCollections({ payload: { deviation: { deviationId  } , deviation , notificationQueue , isPrivateCollections  }  }) {
    const isLoggedIn = yield select(getIsLoggedIn);
    if (!isLoggedIn) {
        yield put(restrictLoggedOut({
            restrictedActionName: ReferralType.FAVOURITE
        }));
        return;
    }
    const { changeType , added , removed , selected , saved , wasFavourite , willBeFavourite , wasPrivatelyCollected , willBePrivatelyCollected  } = yield select(getChangeDetails, deviationId, isPrivateCollections);
    if (changeType === 'none') {
        // nothing changed
        logger.log('No changes found, bailing commit', {
            changeType,
            added,
            removed,
            selected,
            wasFavourite,
            willBeFavourite
        });
        yield put(commitCollectionsSuccess(saved, deviationId, {}));
        return;
    }
    const notification = {
        changeType,
        changedFolderCount: added.length + removed.length,
        exampleCollectionId: added[0] || removed[0],
        collectedDeviation: deviation
    };
    if (isPrivateCollections) {
        if (wasPrivatelyCollected !== willBePrivatelyCollected) {
            yield put(setPrivatelyCollectedState(deviation, willBePrivatelyCollected));
        }
    } else {
        if (wasFavourite !== willBeFavourite) {
            yield put(setFaveState(deviation, willBeFavourite));
        }
    }
    yield put(addNotificationToQueue(notificationQueue, isPrivateCollections ? NotificationType.CollectPrivate : NotificationType.Collect, notification));
    // kick off a save with current state
    let saveProcess = yield fork(saveSelection, deviationId, selected, isPrivateCollections);
    // but also wait for undo
    const { undo  } = yield race({
        undo: take((undoAction)=>undoAction.type === undoChanges.toString() && undoAction.payload && undoAction.payload.deviationId === deviationId),
        wait: delay(UNDO_TIMEOUT)
    });
    if (undo) {
        logger.log('Undo caught');
        const undoNotification = _object_spread({}, notification);
        if (wasFavourite !== willBeFavourite) {
            yield put(setFaveState(deviation, wasFavourite));
        }
        yield put(addNotificationToQueue(notificationQueue, NotificationType.CollectUndo, undoNotification));
        // undoing - cancel previous save and start a new one with the original folders
        yield cancel(saveProcess);
        saveProcess = yield fork(saveSelection, deviationId, saved, isPrivateCollections);
    }
    // wait for the save to finish and process results
    yield join(saveProcess);
    const { failedFolders , successFolders  } = saveProcess.result();
    logger.log('Storing results', {
        failedFolders,
        successFolders
    });
    if (failedFolders.length) {
        yield put(commitCollectionsFail(failedFolders, deviationId));
    }
    const entities = yield select(getUpdatedEntities, deviation, isPrivateCollections);
    yield put(commitCollectionsSuccess(successFolders, deviationId, entities));
}
function* handleCreateCollection(action) {
    if (!action.payload) {
        return;
    }
    const isLoggedIn = yield select(getIsLoggedIn);
    if (!isLoggedIn) {
        yield put(restrictLoggedOut({
            restrictedActionName: ReferralType.FAVOURITE
        }));
        return;
    }
    const { name , selectDeviationId , isPrivateCollections  } = action.payload;
    const result = yield call(doCreateCollection, name, isPrivateCollections);
    if (!result || !result.success || !result.gallection) {
        yield put(createCollectionFail(name));
        return;
    }
    yield all([
        put(createCollectionSuccess(result.gallection.folderId, {
            collections: {
                [result.gallection.folderId]: result.gallection
            }
        })),
        put(withOffset.actions.insertItems({
            streamId: getStreamId(isPrivateCollections),
            items: [
                result.gallection.folderId
            ],
            offset: 1
        })),
        put(toggleCollection(result.gallection.folderId, selectDeviationId, isPrivateCollections))
    ]);
}
function* doChangeFave({ payload: { deviation , deviation: { deviationId  } , to , notificationQueue  }  }) {
    const isLoggedIn = yield select(getIsLoggedIn);
    if (!isLoggedIn) {
        yield put(restrictLoggedOut({
            restrictedActionName: ReferralType.FAVOURITE
        }));
        return;
    }
    const result = yield call(to ? doFave : doUnfave, deviationId);
    const notification = {
        deviation,
        success: result.success
    };
    if (!result.success) {
        yield put(changeFaveFail(deviationId));
        return;
    }
    yield all([
        put(changeFaveSuccess(deviationId, to)),
        put(addNotificationToQueue(notificationQueue, NotificationType.Fave, notification))
    ]);
    yield delay(10000);
    yield put(resetJustFaved(deviationId));
}
export function* openCollectionModal({ payload: { deviation , notificationQueue  }  }) {
    // custom queues should handle it themselves
    if (notificationQueue !== 'default') {
        return;
    }
    const isLoggedIn = yield select(getIsLoggedIn);
    if (!isLoggedIn) {
        yield put(restrictLoggedOut({
            restrictedActionName: ReferralType.FAVOURITE
        }));
        return;
    }
    yield put(pushModal(ModalType.COLLECTION_POPOVER, {
        wrapInModal: false,
        params: {
            collectibleDeviation: deviation,
            ignorePreselectedCollections: true
        }
    }));
}
function* closeCollectionModal() {
    yield put(popModal());
}
export default function* saga() {
    yield all([
        takeEvery(withOffset.actions.STREAM__WITH_OFFSET__FETCH, handleFetchStream),
        takeLatest(fetchCollections, handleFetchCollections),
        takeEvery(initializeDeviation, handleInitializeDeviation),
        takeEvery(commitCollections, handleCommitCollections),
        takeEvery(createCollection, handleCreateCollection),
        takeLatest(changeFave, doChangeFave),
        takeLatest(collectFave, openCollectionModal),
        takeLatest(requestClose, closeCollectionModal)
    ]);
}
